import React, { useEffect, useState } from "react";
import { useI18n, LANGS } from "../i18n";
import { api, setPin as apiSetPin, setSessionToken } from "../lib/api";
import { X, Lock, CheckCircle2 } from "lucide-react";
import Logo from "./Logo";
import GoldoraStrengths from "./GoldoraStrengths";

const STORAGE_KEY = "intelvest_user";
const COOKIE_KEY = "goldora_uid";
const COOKIE_DAYS = 365;

// ---- Cookie helpers (first-party, long-lived; survives iOS Safari ITP) ----
function setUserCookie(user) {
  try {
    const value = encodeURIComponent(JSON.stringify(user));
    const exp = new Date(Date.now() + COOKIE_DAYS * 24 * 60 * 60 * 1000).toUTCString();
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${COOKIE_KEY}=${value}; Expires=${exp}; Path=/; SameSite=Lax${secure}`;
  } catch {/* noop */}
}

function getUserCookie() {
  try {
    const m = document.cookie.match(new RegExp("(?:^|; )" + COOKIE_KEY + "=([^;]*)"));
    if (!m) return null;
    return JSON.parse(decodeURIComponent(m[1]));
  } catch {
    return null;
  }
}

function clearUserCookie() {
  try {
    document.cookie = `${COOKIE_KEY}=; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=/`;
  } catch {/* noop */}
}

export function getStoredUser() {
  // 1) localStorage (preferred)
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.email) return parsed;
    }
  } catch {/* noop */}
  // 2) Cookie fallback (survives iOS Safari/PWA storage cleanup)
  const fromCookie = getUserCookie();
  if (fromCookie?.email) {
    // Restore localStorage so other components see the user immediately
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(fromCookie)); } catch {/* noop */}
    return fromCookie;
  }
  return null;
}

export function clearStoredUser() {
  try { localStorage.removeItem(STORAGE_KEY); } catch {/* noop */}
  clearUserCookie();
}

export default function RegisterModal({ open, onClose, onRegistered, forced = false }) {
  const { t, lang, setLang } = useI18n();
  const [form, setForm] = useState(() => {
    // Pre-fill from cookie/localStorage if available — user just got wiped, save them friction
    const stored = getStoredUser();
    return {
      name: stored?.name || "",
      email: stored?.email || "",
      persona: stored?.persona || "",
      pin: "",
      pin_confirm: "",
    };
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setError("");
      const stored = getStoredUser();
      if (stored && (stored.email || stored.name)) {
        setForm((f) => ({
          name: f.name || stored.name || "",
          email: f.email || stored.email || "",
          persona: f.persona || stored.persona || "",
        }));
      }
    }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (form.name.trim().length < 2) {
      setError(lang === "pt" ? "Informe seu nome." : "Please enter your name.");
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) {
      setError(lang === "pt" ? "E-mail inválido." : "Invalid email.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/users/register", {
        name: form.name.trim(),
        email: form.email.trim(),
        persona: form.persona || null,
        lang,
      });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(res.data));
      setUserCookie(res.data); // long-lived cookie fallback (PWA/iOS Safari)
      onRegistered?.(res.data);
      onClose?.();
    } catch (err) {
      setError(lang === "pt" ? "Não foi possível cadastrar. Tente novamente." : "Registration failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm grid place-items-center p-3 sm:p-4 overflow-y-auto"
      data-testid="register-modal"
      onClick={forced ? undefined : onClose}
    >
      <div
        className="relative surface-elevated w-full max-w-md p-6 sm:p-8 max-h-[92vh] overflow-y-auto my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {!forced && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-neutral-500 hover:text-white"
            data-testid="register-close"
          >
            <X size={18} />
          </button>
        )}

        <div className="flex flex-col items-center text-center mb-5" data-testid="register-brand">
          <Logo size={42} />
          <span className="mt-2 font-brand text-[1.15rem] tracking-[0.18em] brand-metal">GOLDORA</span>
          <span className="mt-1 text-[9px] font-mono uppercase tracking-[0.28em] text-neutral-500">
            {lang === "pt" ? "Visão global. Você no controle." : "Global vision. You in control."}
          </span>
        </div>

        {/* Selling-points block — convinces visitor to complete the form */}
        <div className="mb-6 p-4 rounded-lg bg-gold-500/[0.04] border border-gold-500/20">
          <GoldoraStrengths compact />
        </div>

        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-gold-400">
          <Lock size={11} /> {t.register.privacy}
        </div>
        <h2 className="mt-3 font-display text-2xl sm:text-3xl tracking-tight">{t.register.title}</h2>
        <p className="mt-2 text-sm text-neutral-400">{t.register.sub}</p>

        {/* language quick switch */}
        <div className="mt-4 flex flex-wrap gap-1">
          {LANGS.map((l) => (
            <button
              key={l.code}
              onClick={() => setLang(l.code)}
              className={`px-2 py-1 rounded text-xs font-mono uppercase tracking-wider border ${
                lang === l.code ? "bg-gold-500 border-gold-500 text-black" : "bg-white/5 border-white/10 text-neutral-500 hover:text-white"
              }`}
              data-testid={`register-lang-${l.code}`}
            >
              {l.flag} {l.code.toUpperCase()}
            </button>
          ))}
        </div>

        <form className="mt-6 space-y-3" onSubmit={submit}>
          <Field label={t.register.name}>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2.5 focus:outline-none focus:border-gold-500"
              data-testid="register-name"
              autoComplete="name"
              name="name"
              autoFocus
            />
          </Field>
          <Field label={t.register.email}>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2.5 focus:outline-none focus:border-gold-500"
              data-testid="register-email"
              autoComplete="email"
              name="email"
              inputMode="email"
            />
          </Field>
          <Field label={t.register.persona_label}>
            <div className="grid grid-cols-2 gap-2">
              {(t.register.personas || []).map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setForm({ ...form, persona: p.id })}
                  data-testid={`persona-${p.id}`}
                  className={`text-left text-xs px-2.5 py-2 rounded-md border transition-colors ${
                    form.persona === p.id
                      ? "bg-gold-500/15 border-gold-500/60 text-gold-200"
                      : "bg-white/5 border-white/10 text-neutral-400 hover:border-white/20"
                  }`}
                >
                  {form.persona === p.id && <CheckCircle2 size={11} className="inline mr-1 text-gold-400" />}
                  {p.label}
                </button>
              ))}
            </div>
          </Field>

          <Field label={lang === "pt" ? "PIN de 6 dígitos (acesso seguro)" : "6-digit PIN (secure access)"}>
            <input
              type="password"
              value={form.pin}
              onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "").slice(0, 6) })}
              className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2.5 focus:outline-none focus:border-gold-500 text-center font-mono tracking-[0.6em] text-lg"
              data-testid="register-pin"
              autoComplete="new-password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="••••••"
            />
          </Field>
          <Field label={lang === "pt" ? "Confirme o PIN" : "Confirm PIN"}>
            <input
              type="password"
              value={form.pin_confirm}
              onChange={(e) => setForm({ ...form, pin_confirm: e.target.value.replace(/\D/g, "").slice(0, 6) })}
              className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2.5 focus:outline-none focus:border-gold-500 text-center font-mono tracking-[0.6em] text-lg"
              data-testid="register-pin-confirm"
              autoComplete="new-password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="••••••"
            />
          </Field>

          {error && <div className="text-xs text-red-400 font-mono">{error}</div>}

          <button
            type="submit"
            disabled={submitting}
            data-testid="register-submit"
            className="w-full bg-gold-500 hover:bg-gold-400 text-black font-medium py-3 rounded-md disabled:opacity-40 transition-colors"
          >
            {submitting ? "…" : t.register.submit}
          </button>
        </form>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-mono block mb-1.5">
      {label}
    </label>
    {children}
  </div>
);
